import axios from "axios";

// Pega a variável do ambiente. Se falhar no celular, usa a URL real do preview do agente
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://alugaja.app.br"; 

export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "aj_token_v1";

export function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch (e) { console.error("[api] token read failed:", e); return null; }
}
export function setStoredToken(t) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (e) { console.error("[api] token persist failed:", e); }
}

// withCredentials = false -> no cookies needed; pure Bearer token.
// Funciona em iOS Safari ITP, Chrome Incognito, etc.
// V18.9.13 — `timeout` OBRIGATÓRIO: sem ele, uma requisição que fica pendurada na rede
// móvel nunca rejeita e a tela fica presa em "Salvando.../Criando..." para sempre,
// sem erro nenhum para o usuário.
const api = axios.create({
  baseURL: API,
  withCredentials: false,
  timeout: 45000,
});

// Always attach Bearer token from localStorage when present.
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Só desloga em falha de auth GENUÍNA (token expirado/inválido). Outros 401 (ex.:
// senha errada num fluxo específico, permissão pontual) não apagam a sessão.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    // V18.9.13 — normaliza timeout/queda de rede num `detail` legível. Sem isso, todos
    // os `formatApiError(err.response?.data?.detail)` do app mostravam só
    // "Algo deu errado" (ou nada) quando a requisição nem chegou ao servidor.
    if (!err?.response && (err?.code === "ECONNABORTED" || err?.code === "ERR_NETWORK" || err?.message === "Network Error")) {
      err.response = {
        status: 0,
        data: { detail: "Sem conexão com o servidor. Verifique sua internet e tente novamente." },
      };
    }
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const detail = err?.response?.data?.detail;
    const isAuthFailure =
      typeof detail === "string" &&
      (detail.includes("Sessão expirada") || detail.includes("Token inválido") ||
       detail.includes("Sessão encerrada") ||
       detail.includes("Não autenticado") || detail.includes("Usuário não encontrado"));
    if (status === 401 && isAuthFailure && !url.endsWith("/auth/login") && !url.endsWith("/auth/me")) {
      setStoredToken(null);
    }
    return Promise.reject(err);
  },
);

export default api;

export function fileUrl(path) {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API}/files/${path}`;
}

export function formatBRL(value) {
  return Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

export function formatDateBR(iso) {
  if (!iso) return "";
  try {
    const d = iso.length === 10 ? new Date(iso + "T00:00:00") : new Date(iso);
    return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}

export function formatDateTimeBR(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function formatTimeBR(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export function formatBookingPeriod(b) {
  const startBase = formatDateBR(b.start_date);
  const endBase = formatDateBR(b.end_date);
  // Hora: usa a ativação real se já ativado; senão, a hora ESCOLHIDA pelo cliente (start_time/end_time)
  const startHour = b.timestamp_token_ativacao ? ` às ${formatTimeBR(b.timestamp_token_ativacao)}` : (b.start_time ? ` às ${b.start_time}` : "");
  const endHour = b.timestamp_devolucao_prevista ? ` às ${formatTimeBR(b.timestamp_devolucao_prevista)}` : (b.end_time ? ` às ${b.end_time}` : "");
  // Rótulo: mostra o que é REALMENTE cobrado (diárias/meses), não a contagem inclusiva de dias
  const units = b.time_units || b.days || 1;
  const monthly = String(b.tipo_cobranca || "").toUpperCase() === "MENSAL";
  const unit = monthly ? (units === 1 ? "mês" : "meses") : (units === 1 ? "diária" : "diárias");
  return `De ${startBase}${startHour} até ${endBase}${endHour} · ${units} ${unit}`;
}

// V17.10.2 — traduz mensagens comuns do Pydantic para pt-BR + inclui o CAMPO que falhou
// (facilita o usuário saber exatamente o que preencher).
const PT_MSG = {
  "Input should be a valid number, unable to parse string as a number": "precisa ser um número (deixe vazio para 0 ou digite um valor).",
  "Field required": "é obrigatório.",
  "Input should be a valid string": "precisa ser um texto válido.",
  "Input should be a valid integer": "precisa ser um número inteiro.",
  "Input should be greater than or equal to 0": "precisa ser 0 ou maior.",
  "String should match pattern": "não está no formato aceito.",
  "Input should be": "está com valor inválido.",
};
const _translatePydanticMsg = (raw) => {
  if (!raw || typeof raw !== "string") return String(raw || "");
  for (const [en, ptBr] of Object.entries(PT_MSG)) {
    if (raw.startsWith(en) || raw.includes(en)) return ptBr;
  }
  return raw;
};
const _prettyFieldLoc = (loc) => {
  if (!Array.isArray(loc)) return "";
  const parts = loc.filter((x, i) => !(i === 0 && x === "body"));
  const NAMES = {
    title: "Título", description: "Descrição", price_per_day: "Preço",
    tags: "Palavras-chave", images: "Fotos", address_id: "Endereço",
    valor_frete: "Valor do frete", regras_entrega: "Entrega",
    quantidade_total: "Quantidade em estoque", combos: "Combos",
    complements: "Complementos", caucao: "Caução",
    discount_pct: "Desconto (%)", combo_bundle_discount_pct: "Desconto do kit único (%)",
    combo_progressive: "Escala progressiva", combo_upsell_mode: "Modo do combo",
    combo_stack_percentages: "Somar % do combo", qty_extras: "Quantidade de extras",
    price: "Preço", max_qty: "Quantidade máxima", stock_qty: "Estoque",
    require_ratio: "Proporção obrigatória", require_per_base_qty: "A cada N unidades",
    group_key: "Grupo", group_mode: "Regra do grupo", group_max: "Máximo do grupo",
  };
  return parts
    .map((p, i) => {
      if (typeof p === "number") return `#${p + 1}`;
      return NAMES[p] || p;
    })
    .join(" → ");
};

export function formatApiError(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (!e) return "";
        const field = _prettyFieldLoc(e.loc);
        const msg = _translatePydanticMsg(e.msg || "");
        return field ? `Campo "${field}" ${msg}` : msg;
      })
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return _translatePydanticMsg(detail.msg);
  return String(detail);
}